package uk.co.nickthecoder.kyd

import uk.co.nickthecoder.glok.backend.Resources
import uk.co.nickthecoder.glok.collections.asMutableObservableList
import uk.co.nickthecoder.kyd.appearance.Graphic
import uk.co.nickthecoder.kyd.appearance.Pose
import uk.co.nickthecoder.kyd.util.Vector2

/**
 * The root node of a game's tree of resources.
 * Each node in this tree implements the [Resource] interface.
 *
 * A [Game] is saved as a `.kyd` file which uses a JSON format.
 *
 *
 */
class Game(

    val filePath: String,
    val resources: Resources

) : AbstractParentOrGame() {

    override val resourceType: ResourceType
        get() = ResourceType.GAME

    override val parent get() = null

    override val name: String
        get() = pathNameWithoutExtension(filePath)

    var title: String = "<new game>"

    var initialActPath: String = ""

    /**
     * The size of window we will try to create when the game starts.
     * The actual window size may be larger or smaller.
     */
    var nominalSize: Vector2 = Vector2(1000f, 800f)

    /**
     * Where the center of world is relative as a proportion to the window's size.
     * e.g. (0.5,0.5) will make world point (0,0) appear in the middle of the window.
     * (0,0) will make world point (0,0) appear at the top left of the window.
     *
     * X and Y should be in the range 0..1 (unless you game is weird!).
     */
    var viewPinRatio = Vector2(0.5f, 0.5f)

    var sizeConstraint = SizeConstraint.NONE

    var managementType: String = ManagementClassName
        set(v) {
            field = v
            management = KydTypeRegistry.createManagement(v, this)
        }

    var management: Management = Management(this)
        private set

    val jars = mutableListOf<String>()

    /**
     * Describes how the [Stage]s are arranged.
     * In a very simple game, there is only 1 [StageDescription] (with a default name of `main`).
     * However, more complicated games may have many stages, stacked on top of each other.
     * For example, we may have a bottom layer (index==0) which only contains terrain.
     * Then a middle layer (index==1) for most of the active [Actor]s.
     * Then a layer above that for [Actor]s which are always above the active layers.
     * Maybe this is used for special effects etc.
     *
     * In addition, it is common for the last stage to be what I call the `glass` layer.
     * which shows the score, health etc.
     * If the world can be panned or zoomed, then all the layers _except_ the `glass` layer
     * should pan and zoom at the same time. The `glass` layer never pans, nor zooms.
     * To achieve this, set [StageDescription.linked] to `true` on all bar `glass`.
     *
     * For parallax scrolling games, set [StageDescription.linked] to `false`, and adjust
     * the panning of each stage yourself.
     *
     * Bear in mind, there is only one list of [StageDescription]s for the entire game (all [Act]s),
     * In each [Act] some [Stage]s may be completely empty. This is normal, and doesn't impact performance.
     */
    val stageDescriptions = mutableListOf<StageDescription>()

    /**
     *Determines if zOrder fields are included in the editor. Only enable this if you use [ZOrderStage]s.
     */
    var enableZOrder = false

    override fun game() = this

    override fun path() = "/"

    override fun pathWithTrailingSlash() = "/"


    /**
     * This is only needed in the editor, not during game-play.
     * Each [PickGroup] contains a list of items which can be `picked` and dropped onto a stage,
     * creating an Actor.
     */
    val pickGroups = mutableListOf<PickGroup>().asMutableObservableList()

    /**
     * A map of old-paths to new-paths.
     * When resources are renamed, we remember this old path and map it to its new path.
     * Later, acts can be loaded, and any references to old-paths can be fixed, and the act saved.
     * This map can then be cleared.
     */
    val renamedPaths = mutableMapOf<String, String>()

    /**
     * Called immediately after a game is loaded from a .kyd file.
     * It ensures that [Resource]s such as [Pose] which link to other [Resource]s are linked.
     * In the case of [Pose], its `graphicPath` is used to find the appropriate [Graphic].
     *
     * @return A list of error messages, or an empty list on success.
     */
    fun resolveReferences(): List<String> {
        //println("Game.resolveReferences")
        management = KydTypeRegistry.createManagement(managementType, this)
        //println("Management $management from $managementType")

        val problems = mutableListOf<String>()
        walkTree { resource ->
            if (resource is ResolvableResource) {
                resource.resolveReferences()?.let { problems.add(it) }
            }
        }
        return problems
    }

    fun createStage(stageName: String): Stage {
        val description = stageDescriptions.firstOrNull { it.stageName == stageName }
        return if (description == null) {
            Stage(stageName)
        } else {
            KydTypeRegistry.createStage(description.stageType, stageName)
        }
    }

    override fun dangerousRename(newName: String) {
        throw IllegalArgumentException("Cannot rename a Game")
    }


    fun resolvePath(path: String, relativeTo: Resource = this): String {
        val absPath = absolutePath(path, relativeTo)
        if (find(absPath) != null) {
            return absPath
        }
        //println("Is $absPath renamed?")
        renamedPaths[absPath]?.let { return it }
        //println("Not directly")

        val (parent, name) = pathSplitParent(absPath) ?: return path
        //println("Split into $parent , $name")
        val resolvedParent = resolvePath(parent)
        if (resolvedParent != parent) {
            //println("Resolved $absPath to $resolvedParent/$name")
            return resolvePath("$resolvedParent/$name")
        }
        return absPath
    }

    override fun toString() = "Game $name ($filePath)"
}
